import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import * as Cesium from 'cesium'
import 'cesium/Build/Cesium/Widgets/widgets.css'
import { dataApi } from '../api/client'
import { useAuth } from '../context/AuthContext'
import ModeSelectModal from '../components/ModeSelectModal'
import SimpleRegisterModal from '../components/SimpleRegisterModal'
import AdvancedRegisterModal from '../components/AdvancedRegisterModal'
import UserInfoModal from '../components/UserInfoModal'
import { useMeasurement } from '../hooks/useMeasurement'
import type { MeasureTool } from '../hooks/useMeasurement'
import type { MeshData } from '../types'
import VWorldBasemapSelector, { applyVWorldBasemap } from '../components/VWorldBasemapSelector'
import EmailPromptModal from '../components/EmailPromptModal'
import LanguageSwitcher from '../components/LanguageSwitcher'
import MultiSourceMergeModal from '../components/MultiSourceMergeModal'
import CdSidebar from '../components/changeDetection/CdSidebar'
import { checkSimulationSupport } from '../simulation/fluidModule'
import { watchTileFailures } from '../simulation/tileFailures'

// 유량(침수) 시뮬레이션 - magoFluid 모듈과 셰이더는 처음 열 때만 내려받는다.
const SimulationPanel = lazy(() => import('../components/simulation/SimulationPanel'))
import {
  isLasSource, POINT_STYLE_MODES, DEFAULT_POINT_STYLE, applyPointCloudStyle, applyOpacityOnlyStyle,
  LEGEND_MODES, CLASSIFICATION_LEGEND, INTENSITY_HEAT_LEGEND, HEIGHT_LEGEND_STOPS, getHeightReference,
} from '../utils/pointCloudStyle'
import type { PointStyleState } from '../utils/pointCloudStyle'

type DeleteTarget = { id: number; name: string; hasOriginalFile: boolean }

function DeleteConfirmModal({
  target, onDeleteOriginal, onDeleteAll, onCancel,
}: {
  target: DeleteTarget
  onDeleteOriginal: () => void
  onDeleteAll: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm mx-4 shadow-2xl p-6 space-y-5">
        <div>
          <h2 className="text-base font-semibold text-white">{t('dashboard.deleteModal.title')}</h2>
          <p className="text-sm text-slate-400 mt-1">
            <Trans i18nKey="dashboard.deleteModal.ask" values={{ name: target.name }} components={{ name: <span className="text-white font-medium" /> }} />
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={onDeleteOriginal}
            disabled={!target.hasOriginalFile}
            className="w-full text-left p-4 rounded-xl border border-slate-700 hover:border-amber-600 hover:bg-amber-900/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <p className="font-semibold text-amber-300 text-sm">{t('dashboard.deleteModal.originalOnly')}</p>
            <p className="text-xs text-slate-400 mt-1">
              {t('dashboard.deleteModal.originalOnlyDesc')}
              {!target.hasOriginalFile && <span className="text-slate-500 ml-1">{t('dashboard.deleteModal.alreadyDeleted')}</span>}
            </p>
          </button>

          <button
            onClick={onDeleteAll}
            className="w-full text-left p-4 rounded-xl border border-slate-700 hover:border-red-600 hover:bg-red-900/20 transition-all"
          >
            <p className="font-semibold text-red-400 text-sm">{t('dashboard.deleteModal.all')}</p>
            <p className="text-xs text-slate-400 mt-1">
              {t('dashboard.deleteModal.allDesc')}
            </p>
          </button>
        </div>

        <button
          onClick={onCancel}
          className="w-full py-2 text-sm text-slate-400 hover:text-white transition-colors"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  )
}

type HeightOffsetTarget = { id: number; name: string }

function HeightOffsetModal({
  target, onSubmit, onCancel, submitting,
}: {
  target: HeightOffsetTarget
  onSubmit: (deltaMeters: number) => void
  onCancel: () => void
  submitting: boolean
}) {
  const { t } = useTranslation()
  const [delta, setDelta] = useState('')
  const parsed = Number(delta)
  const valid = delta.trim() !== '' && !Number.isNaN(parsed) && parsed !== 0

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm mx-4 shadow-2xl p-6 space-y-5">
        <div>
          <h2 className="text-base font-semibold text-white">{t('dashboard.heightModal.title')}</h2>
          <p className="text-sm text-slate-400 mt-1">
            <Trans i18nKey="dashboard.heightModal.desc" values={{ name: target.name }} components={{ name: <span className="text-white font-medium" /> }} />
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('dashboard.heightModal.label')}</label>
          <input
            type="number" step="0.1" value={delta} onChange={e => setDelta(e.target.value)}
            placeholder={t('dashboard.heightModal.placeholder')}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter' && valid && !submitting) onSubmit(parsed) }}
            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
          />
          <p className="text-[11px] text-amber-400 mt-2">
            {t('dashboard.heightModal.note')}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 py-2 text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-40"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => valid && onSubmit(parsed)}
            disabled={!valid || submitting}
            className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? t('dashboard.heightModal.applying') : t('dashboard.heightModal.apply')}
          </button>
        </div>
      </div>
    </div>
  )
}

function MergeTilesetsModal({
  candidates, onSubmit, onCancel, submitting,
}: {
  candidates: MeshData[]
  onSubmit: (data: { name: string; description?: string; dataIds: number[] }) => void
  onCancel: () => void
  submitting: boolean
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const toggle = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const valid = name.trim() !== '' && selectedIds.size >= 2

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md mx-4 shadow-2xl p-6 space-y-5">
        <div>
          <h2 className="text-base font-semibold text-white">{t('dashboard.mergeModal.title')}</h2>
          <p className="text-sm text-slate-400 mt-1">
            {t('dashboard.mergeModal.desc')}
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('dashboard.mergeModal.nameLabel')}</label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder={t('dashboard.mergeModal.namePlaceholder')}
              autoFocus
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('dashboard.mergeModal.descLabel')}</label>
            <input
              type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder={t('dashboard.mergeModal.descPlaceholder')}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">
              {t('dashboard.mergeModal.pick', { count: selectedIds.size })}
            </label>
            {candidates.length === 0 ? (
              <p className="text-xs text-slate-500 py-3 text-center">{t('dashboard.mergeModal.none')}</p>
            ) : (
              <ul className="max-h-56 overflow-y-auto border border-slate-700 rounded-lg divide-y divide-slate-700">
                {candidates.map(item => (
                  <li key={item.id}>
                    <label className="flex items-center gap-2 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggle(item.id)}
                        className="accent-blue-500"
                      />
                      <span className="truncate">{item.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 py-2 text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-40"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => valid && onSubmit({ name: name.trim(), description: description.trim() || undefined, dataIds: [...selectedIds] })}
            disabled={!valid || submitting}
            className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? t('dashboard.mergeModal.merging') : t('dashboard.mergeModal.merge')}
          </button>
        </div>
      </div>
    </div>
  )
}

const STATUS_LABEL: Record<string, { key: 'PENDING' | 'CONVERTING' | 'SUCCESS' | 'FAILED'; color: string }> = {
  PENDING: { key: 'PENDING', color: 'bg-gray-100 text-gray-600' },
  CONVERTING: { key: 'CONVERTING', color: 'bg-yellow-100 text-yellow-700' },
  SUCCESS: { key: 'SUCCESS', color: 'bg-green-100 text-green-700' },
  FAILED: { key: 'FAILED', color: 'bg-red-100 text-red-700' },
}

export default function DashboardPage() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const mapRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<Cesium.Viewer | null>(null)
  const tilesetsRef = useRef<Map<number, Cesium.Cesium3DTileset>>(new Map())
  const [visibleIds, setVisibleIds] = useState<Set<number>>(new Set())
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set())
  const opacitiesRef = useRef<Record<number, number>>({})
  const [opacities, setOpacitiesState] = useState<Record<number, number>>({})
  const pointStylesRef = useRef<Record<number, PointStyleState>>({})
  const [pointStyles, setPointStylesState] = useState<Record<number, PointStyleState>>({})

  const [items, setItems] = useState<MeshData[]>([])
  const [search, setSearch] = useState('')
  const [activeModal, setActiveModal] = useState<null | 'select' | 'simple' | 'advanced'>(null)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [deleteModal, setDeleteModal] = useState<DeleteTarget | null>(null)
  const [heightOffsetModal, setHeightOffsetModal] = useState<HeightOffsetTarget | null>(null)
  const [applyingHeightOffset, setApplyingHeightOffset] = useState(false)
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [merging, setMerging] = useState(false)
  const [showMultiMergeModal, setShowMultiMergeModal] = useState(false)
  const [showUserInfo, setShowUserInfo] = useState(false)
  const [activeTab, setActiveTab] = useState<'data' | 'cd'>('data')
  // 유량 시뮬레이션: 대상 메쉬와, 시뮬레이션을 위해 숨긴 다른 타일셋(종료 때 복원)
  const [simulationTarget, setSimulationTarget] = useState<MeshData | null>(null)
  const simulationHiddenRef = useRef<number[]>([])
  // 타일셋별로 내용을 불러오지 못한 타일 수 (시뮬레이션이 깨진 데이터에서 멈추지 않게 미리 알리는 용도)
  const [tileFailureCounts, setTileFailureCounts] = useState<Record<number, number>>({})
  const [sidebarWidth, setSidebarWidth] = useState(300)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const isDragging = useRef(false)
  const dragStartX = useRef(0)
  const dragStartWidth = useRef(0)

  const {
    activeTool: measureTool,
    measureResult,
    showVolumeInput,
    volumeHeight,
    setVolumeHeight,
    canFinish,
    startMeasure,
    stopMeasure,
    finishMeasure,
    computeVolume,
    isCalcMesh,
  } = useMeasurement(viewerRef, visibleIds.size > 0)

  // Cesium 뷰어 초기화
  useEffect(() => {
    if (!mapRef.current) return

    const viewer = new Cesium.Viewer(mapRef.current, {
      baseLayerPicker: false,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      navigationHelpButton: false,
      sceneModePicker: false,
      animation: false,
      timeline: false,
      fullscreenButton: false,
      geocoder: false,
      homeButton: false,
      selectionIndicator: false,
      infoBox: false,
    })
    viewerRef.current = viewer

    let isMounted = true
    const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8080/api'
    const stored = localStorage.getItem('auth')
    const token: string | undefined = stored ? JSON.parse(stored).token : undefined
    const terrainResource = new Cesium.Resource({
      url: `${apiUrl}/terrain`,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    Cesium.CesiumTerrainProvider.fromUrl(terrainResource
    ).then(terrainProvider => {
      if (!isMounted || viewer.isDestroyed()) {
        console.log('Viewer가 이미 파괴된 상태에서 Terrain이 로드되었습니다.')
        return
      }
      viewer.terrainProvider = terrainProvider
      console.log('Terrain이 성공적으로 로드되었습니다.')
    }).catch((e: unknown) => console.error('Terrain 로드 실패:', e))

    applyVWorldBasemap(viewer, 'Base')

    // 한반도 초기 뷰
    viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(127.8, 37.5, 1600000),
      orientation: {
        heading: Cesium.Math.toRadians(0),
        pitch: Cesium.Math.toRadians(-90),
        roll: 0,
      },
    })

    return () => {
      if (!viewer.isDestroyed()) viewer.destroy()
    }
  }, [])

  // 타일셋 스타일 일괄 재적용 — LAS 포인트 클라우드는 색상/점크기만(투명도 미지원), 그 외는 투명도만
  const applyTilesetStyle = (item: MeshData) => {
    const tileset = tilesetsRef.current.get(item.id)
    if (!tileset || tileset.isDestroyed()) return
    if (isLasSource(item.originalFileName)) {
      applyPointCloudStyle(tileset, pointStylesRef.current[item.id] ?? DEFAULT_POINT_STYLE)
    } else {
      applyOpacityOnlyStyle(tileset, opacitiesRef.current[item.id] ?? 100)
    }
  }

  const getOpacity = (id: number) => opacities[id] ?? 100
  const getPointStyle = (id: number) => pointStyles[id] ?? DEFAULT_POINT_STYLE

  const handleOpacityChange = (item: MeshData, value: number) => {
    opacitiesRef.current = { ...opacitiesRef.current, [item.id]: value }
    setOpacitiesState(opacitiesRef.current)
    applyTilesetStyle(item)
  }

  // 포인트 클라우드는 tileset.style/customShader를 바꿔도 이미 로드된 타일 콘텐츠에는 반영되지 않음
  // (CesiumJS가 비-pnts glTF 포인트 클라우드는 feature/배치 테이블이 없으면 셰이더를 다시 빌드하지
  // 않는 내부 제약 — 실측 결과 새로 로드되는 타일에만 적용되고 기존 타일은 그대로 남음). 타일셋을
  // 제거 후 재로드해 모든 타일에 강제로 반영한다.
  const handlePointStyleChange = async (item: MeshData, patch: Partial<PointStyleState>) => {
    const next = { ...getPointStyle(item.id), ...patch }
    pointStylesRef.current = { ...pointStylesRef.current, [item.id]: next }
    setPointStylesState(pointStylesRef.current)
    if (tilesetsRef.current.has(item.id)) {
      removeTilesetFromViewer(item.id)
      await toggleTileset(item)
    }
  }

  // 3D Tiles On/Off 토글
  const toggleTileset = useCallback(async (item: MeshData) => {
    const viewer = viewerRef.current
    if (!viewer || viewer.isDestroyed() || !item.tileUrl) return

    const existing = tilesetsRef.current.get(item.id)
    if (existing && !existing.isDestroyed()) {
      const willShow = !existing.show
      existing.show = willShow
      setVisibleIds(prev => {
        const next = new Set(prev)
        if (willShow) { next.add(item.id); viewer.zoomTo(existing) }
        else next.delete(item.id)
        return next
      })
      return
    }

    setLoadingIds(prev => new Set(prev).add(item.id))

    const isProxied = item.tileUrl.startsWith('/')
    const stored = localStorage.getItem('auth')
    const token: string | undefined = stored ? JSON.parse(stored).token : undefined
    const tileResource = new Cesium.Resource({
      url: isProxied ? `${window.location.origin}${item.tileUrl}` : item.tileUrl,
      headers: isProxied && token ? { Authorization: `Bearer ${token}` } : {},
    })

    try {
      const tileset = await Cesium.Cesium3DTileset.fromUrl(tileResource)
      if (!viewerRef.current || viewerRef.current.isDestroyed()) return

      // Gaussian Splat 원본 데이터는 실제 지형 고도가 아닌 로컬/임의 기준 높이로 구워지는
      // 경우가 많아, 실제 지형(terrain) 위에 올리면 지형 아래에 파묻혀 안 보일 수 있다
      // (simple_viewer는 EllipsoidTerrainProvider만 써서 이 문제가 드러나지 않았을 뿐).
      // 타일셋 중심의 실제 지형 고도를 샘플링해 그 차이만큼 위로 띄워 보정한다.
      if (Cesium.GaussianSplat3DTileContent.tilesetRequiresGaussianSplattingExt(tileset)) {
        try {
          const center = Cesium.Cartographic.fromCartesian(tileset.boundingSphere.center)
          const [sampled] = await Cesium.sampleTerrainMostDetailed(viewerRef.current.terrainProvider, [Cesium.Cartographic.clone(center)])
          const diff = (sampled.height ?? 0) - center.height
          if (!viewerRef.current || viewerRef.current.isDestroyed()) return
          if (Number.isFinite(diff) && Math.abs(diff) > 0.5) {
            const surfaceNormal = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(tileset.boundingSphere.center, new Cesium.Cartesian3())
            const translation = Cesium.Cartesian3.multiplyByScalar(surfaceNormal, diff, new Cesium.Cartesian3())
            tileset.modelMatrix = Cesium.Matrix4.multiply(Cesium.Matrix4.fromTranslation(translation), tileset.modelMatrix, new Cesium.Matrix4())
            console.log(`[3DGS 높이 보정] "${item.name}" 지형 고도와 ${diff.toFixed(1)}m 차이 감지 — 자동 보정 적용`)
          }
        } catch (e) {
          console.warn('3DGS 지형 높이 보정 실패(원본 높이로 표시):', e)
        }
      }

      watchTileFailures(tileset, (count) =>
        setTileFailureCounts((prev) => (prev[item.id] === count ? prev : { ...prev, [item.id]: count })),
      )
      viewerRef.current.scene.primitives.add(tileset)
      tilesetsRef.current.set(item.id, tileset)
      applyTilesetStyle(item)
      setVisibleIds(prev => new Set(prev).add(item.id))
      viewerRef.current.zoomTo(tileset)
    } catch (e) {
      console.error('3D Tiles 로드 실패:', e)
    } finally {
      setLoadingIds(prev => { const next = new Set(prev); next.delete(item.id); return next })
    }
  }, [])

  // 항목 클릭 시 해당 타일 위치로 이동 (미로드 시 자동 로드 후 줌)
  const flyToTileset = useCallback(async (item: MeshData) => {
    const viewer = viewerRef.current
    if (!viewer || viewer.isDestroyed() || !item.tileUrl) return

    const existing = tilesetsRef.current.get(item.id)
    if (existing && !existing.isDestroyed()) {
      if (!existing.show) {
        existing.show = true
        setVisibleIds(prev => new Set(prev).add(item.id))
      }
      viewer.zoomTo(existing)
    } else {
      await toggleTileset(item)
    }
  }, [toggleTileset])

  // 삭제·업로드 완료 후 명시적 재조회용 (폴링 없음)
  const fetchData = useCallback(async () => {
    setLoading(true)
    setFetchError(null)
    try {
      const res = await dataApi.list({ name: search || undefined, page: 0, size: 100 })
      setItems(res.content)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string }; status?: number } }
      if (err.response?.status !== 401) {
        setFetchError(err.response?.data?.error ?? t('dashboard.toast.fetchFailed'))
      }
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => { fetchData() }, [fetchData])

  const removeTilesetFromViewer = (id: number) => {
    const tileset = tilesetsRef.current.get(id)
    if (tileset && !tileset.isDestroyed()) {
      const viewer = viewerRef.current
      if (viewer && !viewer.isDestroyed()) viewer.scene.primitives.remove(tileset)
    }
    tilesetsRef.current.delete(id)
    setVisibleIds(prev => { const next = new Set(prev); next.delete(id); return next })
  }

  const handleDeleteOriginal = async () => {
    if (!deleteModal) return
    const { id } = deleteModal
    setDeleteModal(null)
    try {
      const updated = await dataApi.deleteOriginal(id)
      setItems(prev => prev.map(item => item.id === id ? { ...item, hasOriginalFile: updated.hasOriginalFile } : item))
      showToast(t('dashboard.toast.originalDeleted'))
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      showToast(err.response?.data?.error ?? t('common.deleteFailed'))
    }
  }

  const handleDeleteAll = async () => {
    if (!deleteModal) return
    const { id } = deleteModal
    setDeleteModal(null)
    try {
      await dataApi.delete(id)
      removeTilesetFromViewer(id)
      fetchData()
      showToast(t('dashboard.toast.dataDeleted'))
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      showToast(err.response?.data?.error ?? t('common.deleteFailed'))
    }
  }

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  // ── 유량(침수) 시뮬레이션 ──
  // 내가 업로드한 메쉬 데이터 하나 위에서만 연다. 측정 도구·변화탐지 탭과는 함께 쓰지 않는다.
  const openSimulation = async (item: MeshData) => {
    if (simulationTarget || item.sample || item.status !== 'SUCCESS' || !item.tileUrl) return
    const support = checkSimulationSupport()
    if (!support.ok) {
      showToast(t(`simulation.unsupported.${support.reason ?? 'noWebgl'}`))
      return
    }
    stopMeasure()
    setActiveTab('data')

    // 대상 타일셋을 먼저 불러와 보이게 한다 (toggleTileset 은 보이는 상태면 숨기므로 숨겨졌거나 없을 때만 부른다).
    let tileset = tilesetsRef.current.get(item.id)
    if (!tileset || tileset.isDestroyed() || !tileset.show) {
      await toggleTileset(item)
      tileset = tilesetsRef.current.get(item.id)
    }
    if (!tileset || tileset.isDestroyed()) return
    viewerRef.current?.zoomTo(tileset)

    // 높이·DSM 스캔과 결과 화면이 다른 데이터에 섞이지 않도록 다른 타일셋은 잠시 숨긴다.
    const hidden: number[] = []
    tilesetsRef.current.forEach((other, id) => {
      if (id !== item.id && !other.isDestroyed() && other.show) {
        other.show = false
        hidden.push(id)
      }
    })
    simulationHiddenRef.current = hidden
    setVisibleIds(new Set([item.id]))
    setSimulationTarget(item)
  }

  const closeSimulation = useCallback(() => {
    const hidden = simulationHiddenRef.current
    simulationHiddenRef.current = []
    const restored: number[] = []
    hidden.forEach(id => {
      const tileset = tilesetsRef.current.get(id)
      if (tileset && !tileset.isDestroyed()) {
        tileset.show = true
        restored.push(id)
      }
    })
    if (restored.length > 0) setVisibleIds(prev => new Set([...prev, ...restored]))
    setSimulationTarget(null)
  }, [])

  // 대상 데이터가 목록에서 사라지면(삭제 등) 시뮬레이션도 끝낸다.
  useEffect(() => {
    if (simulationTarget && !loading && !items.some(i => i.id === simulationTarget.id)) closeSimulation()
  }, [items, loading, simulationTarget, closeSimulation])

  // 재변환 없이 저장된 tileset.json 자체를 수정해 높이(고도)를 영구 보정
  const handleApplyHeightOffset = async (deltaMeters: number) => {
    if (!heightOffsetModal) return
    const { id } = heightOffsetModal
    setApplyingHeightOffset(true)
    try {
      const { totalOffsetMeters } = await dataApi.adjustHeightOffset(id, deltaMeters)
      setHeightOffsetModal(null)
      showToast(t('dashboard.toast.heightApplied', { meters: totalOffsetMeters.toFixed(2) }))
      // 뷰어에 이미 로드돼 있으면 갱신된 tileset.json을 다시 받아오도록 제거 후 재로드
      const item = items.find(i => i.id === id)
      if (item && tilesetsRef.current.has(id)) {
        removeTilesetFromViewer(id)
        await toggleTileset(item)
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      showToast(err.response?.data?.error ?? t('dashboard.toast.heightFailed'))
    } finally {
      setApplyingHeightOffset(false)
    }
  }

  const handleMergeTilesets = async (data: { name: string; description?: string; dataIds: number[] }) => {
    setMerging(true)
    try {
      await dataApi.mergeTilesets(data)
      setShowMergeModal(false)
      showToast(t('dashboard.toast.merged'))
      fetchData()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      showToast(err.response?.data?.error ?? t('dashboard.toast.mergeFailed'))
    } finally {
      setMerging(false)
    }
  }

  const handleCopyUrl = async (item: MeshData) => {
    if (!item.tileUrl) return
    try {
      if (item.tileUrl.startsWith('/')) {
        const { token } = await dataApi.getTileToken(item.id)
        const base = `${window.location.origin}${item.tileUrl}`
        await navigator.clipboard.writeText(`${base}?token=${token}`)
      } else {
        await navigator.clipboard.writeText(item.tileUrl)
      }
      showToast(t('dashboard.toast.urlCopied'))
    } catch {
      showToast(t('dashboard.toast.urlCopyFailed'))
    }
  }

  const handleDownload = async (item: MeshData) => {
    try {
      await dataApi.download(item.id, item.name)
    } catch {
      alert(t('dashboard.toast.downloadFailed'))
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    isDragging.current = true
    dragStartX.current = e.clientX
    dragStartWidth.current = sidebarWidth
    e.preventDefault()
  }, [sidebarWidth])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging.current) return
      const delta = dragStartX.current - e.clientX
      setSidebarWidth(Math.min(600, Math.max(200, dragStartWidth.current + delta)))
    }
    const onUp = () => { isDragging.current = false }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [])

  const filteredItems = search
    ? items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()))
    : items

  // 뷰어에 표시 중인 LAS 항목 중 범례가 필요한 스타일(분류·반사강도 히트맵·높이)을 쓰는 첫 항목을 찾아 범례로 표시
  const legendItem = items.find(item =>
    visibleIds.has(item.id) && isLasSource(item.originalFileName)
    && LEGEND_MODES.includes(getPointStyle(item.id).mode))
  const legendHeightRange = legendItem && getPointStyle(legendItem.id).mode === 'height'
    ? (() => {
      const tileset = tilesetsRef.current.get(legendItem.id)
      return tileset && !tileset.isDestroyed() ? getHeightReference(tileset) : null
    })()
    : null

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* 헤더 */}
      <header className="h-12 bg-slate-900 flex items-center justify-between px-4 shrink-0 z-10">
        <div className="flex items-center gap-3">
          <img src="/gaia3d-logo.png" alt="Gaia3D" className="h-6 object-contain" />
          <span className="text-white text-sm font-semibold hidden sm:block">Mago 3D Reality Service</span>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher tone="dark" />
          <span className="text-slate-400 text-xs">{user?.name} ({user?.userId})</span>
          <button
            onClick={() => navigate('/manual')}
            title={t('dashboard.header.manualTitle')}
            className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded px-2 py-1 transition-colors flex items-center gap-1"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            {t('dashboard.header.manual')}
          </button>
          <button
            onClick={() => navigate('/support')}
            title={t('dashboard.header.supportTitle')}
            className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded px-2 py-1 transition-colors flex items-center gap-1"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            {t('dashboard.header.support')}
          </button>
          <button
            onClick={() => setShowUserInfo(true)}
            title={t('dashboard.header.userInfoTitle')}
            className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded px-2 py-1 transition-colors flex items-center gap-1"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
            {t('dashboard.header.userInfo')}
          </button>
          <UserInfoModal open={showUserInfo} onClose={() => setShowUserInfo(false)} />
          <button
            onClick={() => navigate('/terms')}
            title={t('dashboard.header.termsTitle')}
            className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded px-2 py-1 transition-colors flex items-center gap-1"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            {t('dashboard.header.terms')}
          </button>
          <button
            onClick={handleLogout}
            className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded px-2 py-1 transition-colors"
          >
            {t('common.logout')}
          </button>
        </div>
      </header>

      {/* 콘텐츠 영역 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

        {/* 지도 */}
        <div ref={mapRef} style={{ flex: 1, position: 'relative' }}>
          {loadingIds.size > 0 && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 bg-slate-900/80 text-white text-xs px-3 py-1.5 rounded-full">
              {t('dashboard.tilesetLoading')}
            </div>
          )}
          {toast && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-green-700/90 text-white text-xs px-3 py-1.5 rounded-full whitespace-nowrap">
              {toast}
            </div>
          )}

          {simulationTarget && (
            <Suspense fallback={null}>
              <SimulationPanel
                viewerRef={viewerRef}
                target={simulationTarget}
                getTileset={() => tilesetsRef.current.get(simulationTarget.id)}
                failedTileCount={tileFailureCounts[simulationTarget.id] ?? 0}
                onClose={closeSimulation}
              />
            </Suspense>
          )}

          <VWorldBasemapSelector viewerRef={viewerRef} />

          {/* 측정 도구 툴바 + (있다면) 포인트 클라우드 범례 */}
          <div className="absolute bottom-6 left-4 z-10 flex items-end gap-2">
            <div className="select-none flex flex-col gap-2 items-start">
              <div className="bg-slate-900/90 backdrop-blur-sm rounded-lg shadow-xl overflow-hidden text-white" style={{ minWidth: 138 }}>
                <div className="px-3 py-1.5 bg-slate-800/60 border-b border-slate-700">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('dashboard.measure.title')}</span>
                </div>
                <div className="p-1.5 flex flex-col gap-0.5">
                  {([
                    {
                      key: 'distance' as MeasureTool, label: t('dashboard.measure.distance'), icon: (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="2" y1="12" x2="22" y2="12" /><line x1="2" y1="7" x2="2" y2="17" /><line x1="22" y1="7" x2="22" y2="17" />
                        </svg>
                      )
                    },
                    {
                      key: 'area' as MeasureTool, label: t('dashboard.measure.area'), icon: (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5" />
                        </svg>
                      )
                    },
                    {
                      key: 'volume' as MeasureTool, label: t('dashboard.measure.volume'), icon: (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                          <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
                        </svg>
                      )
                    },
                  ]).map(({ key, label, icon }) => (
                    <button
                      key={key}
                      onClick={() => startMeasure(key)}
                      disabled={!!simulationTarget}
                      title={simulationTarget ? t('dashboard.measure.disabledBySimulation') : undefined}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-[11px] font-medium w-full text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${measureTool === key
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                        }`}
                    >
                      {icon}
                      {label}
                      {measureTool === key && <span className="ml-auto text-[9px] text-blue-200">●</span>}
                    </button>
                  ))}
                </div>
                {(measureTool || measureResult) && (
                  <div className="flex gap-1.5 px-2 pb-2">
                    {measureTool && (
                      <button
                        onClick={finishMeasure}
                        disabled={!canFinish}
                        className="flex-1 text-[10px] py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {t('dashboard.measure.done')}
                      </button>
                    )}
                    <button
                      onClick={stopMeasure}
                      className={`${measureTool ? 'flex-1' : 'w-full'} text-[10px] py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors`}
                    >
                      {t('dashboard.measure.reset')}
                    </button>
                  </div>
                )}
              </div>

              {(measureResult || isCalcMesh) && (
                <div className="bg-slate-900/90 backdrop-blur-sm rounded-lg shadow-xl px-3 py-2 text-white" style={{ minWidth: 138 }}>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1">{t('dashboard.measure.result')}</p>
                  {isCalcMesh ? (
                    <div className="flex items-center gap-1.5 text-[11px] text-emerald-400">
                      <svg className="animate-spin shrink-0" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                      </svg>
                      {t('dashboard.measure.meshCalc')}
                    </div>
                  ) : (
                    <pre className="text-[11px] text-white font-mono whitespace-pre-wrap leading-relaxed">{measureResult}</pre>
                  )}
                </div>
              )}

              {showVolumeInput && (
                <div className="bg-slate-900/90 backdrop-blur-sm rounded-lg shadow-xl p-3 text-white" style={{ minWidth: 160 }}>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-2">{t('dashboard.measure.heightInput')}</p>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number" value={volumeHeight} onChange={e => setVolumeHeight(e.target.value)}
                      min="0.1" step="0.1"
                      onKeyDown={e => e.key === 'Enter' && computeVolume()}
                      className="w-16 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-blue-400"
                    />
                    <span className="text-[11px] text-slate-400">m</span>
                    <button
                      onClick={computeVolume}
                      className="bg-emerald-700 hover:bg-emerald-600 px-2 py-1 rounded text-[10px] text-white font-medium transition-colors"
                    >
                      {t('dashboard.measure.calc')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {legendItem && (
              <div className="bg-slate-900/90 backdrop-blur-sm rounded-lg shadow-xl overflow-hidden text-white" style={{ minWidth: 138 }}>
                <div className="px-3 py-1.5 bg-slate-800/60 border-b border-slate-700">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                    {(() => { const m = POINT_STYLE_MODES.find(x => x.val === getPointStyle(legendItem.id).mode); return m ? t(m.labelKey) : t('dashboard.legend.title') })()}
                  </span>
                </div>
                <div className="p-2.5">
                  {getPointStyle(legendItem.id).mode === 'classification' && (
                    <ul className="space-y-1">
                      {CLASSIFICATION_LEGEND.map(({ code, labelKey, color }) => (
                        <li key={code} className="flex items-center gap-1.5 text-[10px] text-slate-300">
                          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                          {t(labelKey)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {getPointStyle(legendItem.id).mode === 'intensity-heat' && (
                    <ul className="space-y-1">
                      {INTENSITY_HEAT_LEGEND.map(({ threshold, labelKey, color }) => (
                        <li key={threshold} className="flex items-center gap-1.5 text-[10px] text-slate-300">
                          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                          {t(labelKey)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {getPointStyle(legendItem.id).mode === 'height' && (
                    <div style={{ width: 140 }}>
                      <div
                        className="h-3 rounded"
                        style={{ background: `linear-gradient(to right, ${HEIGHT_LEGEND_STOPS.join(', ')})` }}
                      />
                      <div className="flex justify-between text-[9px] text-slate-400 mt-1">
                        <span>{legendHeightRange ? `${legendHeightRange.minHeight.toFixed(1)}m` : t('dashboard.legend.low')}</span>
                        <span>{legendHeightRange ? `${legendHeightRange.maxHeight.toFixed(1)}m` : t('dashboard.legend.high')}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 사이드바 접혔을 때 열기 탭 */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-slate-800 hover:bg-slate-700 border border-slate-600 border-r-0 text-slate-300 hover:text-white rounded-l-lg px-1.5 py-4 transition-colors shadow-lg"
            title={t('dashboard.list.openTitle')}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}

        {/* 리사이즈 핸들 + 사이드바 */}
        {sidebarOpen && (
          <>
            {/* 드래그 리사이즈 핸들 */}
            <div
              onMouseDown={handleResizeStart}
              className="w-1 shrink-0 cursor-col-resize bg-gray-200 hover:bg-blue-400 active:bg-blue-500 transition-colors relative group"
              title={t('dashboard.list.resizeTitle')}
            >
              <div className="absolute inset-y-0 -left-1 -right-1" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-[3px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} className="w-[3px] h-[3px] rounded-full bg-blue-400" />
                ))}
              </div>
            </div>

            {/* 사이드바 본체 */}
            <div
              className="bg-white border-l border-gray-200 flex flex-col shrink-0"
              style={{ width: sidebarWidth }}
            >
              {/* 사이드바 탭 */}
              <div className="flex items-stretch border-b border-gray-200 shrink-0">
                {([
                  { key: 'data', label: t('dashboard.tabs.data') },
                  { key: 'cd', label: t('dashboard.tabs.cd') },
                ] as const).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    disabled={tab.key === 'cd' && !!simulationTarget}
                    title={tab.key === 'cd' && simulationTarget ? t('dashboard.measure.disabledBySimulation') : undefined}
                    className={`flex-1 py-2 text-xs font-semibold border-b-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      activeTab === tab.key
                        ? 'border-blue-600 text-blue-700 bg-blue-50/40'
                        : 'border-transparent text-gray-400 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'cd' && <CdSidebar viewerRef={viewerRef} />}

              {activeTab === 'data' && (
              <>
              {/* 사이드바 헤더 */}
              <div className="px-4 py-3 border-b border-gray-200 shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold text-gray-900">{t('dashboard.list.title')}</h2>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={fetchData}
                      disabled={loading}
                      title={t('dashboard.list.refreshTitle')}
                      className="text-xs text-gray-500 hover:text-blue-600 border border-gray-300 hover:border-blue-400 rounded px-2 py-1 transition-colors disabled:opacity-40"
                    >
                      {loading ? '⟳' : '↺'}
                    </button>
                    <button
                      onClick={() => setActiveModal('select')}
                      className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded transition-colors"
                    >
                      {t('dashboard.list.register')}
                    </button>
                    <button
                      onClick={() => setSidebarOpen(false)}
                      title={t('common.close')}
                      className="text-gray-400 hover:text-gray-700 transition-colors ml-1"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('dashboard.list.searchPlaceholder')}
                  className="w-full text-xs px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* 데이터 목록 */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-24 text-gray-400 text-sm">{t('dashboard.list.loading')}</div>
                ) : fetchError ? (
                  <div className="flex flex-col items-center justify-center h-24 text-red-500 text-xs gap-2 px-4 text-center">
                    <span>{fetchError}</span>
                    <button onClick={fetchData} className="text-blue-500 hover:underline">{t('dashboard.list.retry')}</button>
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-24 text-gray-400 text-sm gap-1">
                    <span>{t('dashboard.list.empty')}</span>
                    <button onClick={() => setActiveModal('select')} className="text-blue-500 hover:underline text-xs">
                      {t('dashboard.list.registerCta')}
                    </button>
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {filteredItems.map((item) => {
                      const st = STATUS_LABEL[item.status]
                      const isToggleable = item.status === 'SUCCESS' && !!item.tileUrl
                      const isVisible = visibleIds.has(item.id)
                      const isLoadingThis = loadingIds.has(item.id)
                      return (
                        <li
                          key={item.id}
                          onClick={() => isToggleable && !simulationTarget && flyToTileset(item)}
                          className={`px-4 py-3 transition-colors ${isToggleable ? 'cursor-pointer' : ''} ${isVisible ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-gray-50'}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                                {item.sample && (
                                  <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium shrink-0">{t('dashboard.list.sample')}</span>
                                )}
                              </div>
                              {item.description && (
                                <p className="text-xs text-gray-500 truncate mt-0.5">{item.description}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${st.color}`}>
                                  {t(`dashboard.status.${st.key}`)}
                                </span>
                                <span className="text-[10px] text-gray-400">
                                  {new Date(item.createdAt).toLocaleDateString('ko-KR')}
                                </span>
                              </div>
                              {isToggleable && (
                                <div className="flex items-center gap-1 mt-1.5" onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={() => handleCopyUrl(item)}
                                    className="text-[10px] px-1.5 py-0.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                                    title={t('dashboard.list.copyUrlTitle')}
                                  >
                                    {t('dashboard.list.copyUrl')}
                                  </button>
                                  {/* TEMP: 테스트를 위해 FREE 등급 다운로드 제한을 임시로 해제함. 테스트 종료 후 비활성화 로직 복원할 것. */}
                                  {/* 샘플 데이터는 기존과 동일하게 다운로드 불가(객체 스토리지에 원본이 없는 정적 데이터) */}
                                  {!item.sample && (
                                    <button
                                      onClick={() => handleDownload(item)}
                                      className="text-[10px] px-1.5 py-0.5 rounded border transition-colors border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                      title={t('dashboard.list.downloadTitle')}
                                    >
                                      {t('dashboard.list.download')}
                                    </button>
                                  )}
                                  {!item.sample && (
                                    <button
                                      onClick={() => setHeightOffsetModal({ id: item.id, name: item.name })}
                                      disabled={!!simulationTarget}
                                      className="text-[10px] px-1.5 py-0.5 rounded border transition-colors border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                      title={t('dashboard.list.heightOffsetTitle')}
                                    >
                                      {t('dashboard.list.heightOffset')}
                                    </button>
                                  )}
                                  {!item.sample && (
                                    <button
                                      onClick={() => void openSimulation(item)}
                                      disabled={!!simulationTarget}
                                      className="text-[10px] px-1.5 py-0.5 rounded border transition-colors border-sky-200 text-sky-600 hover:bg-sky-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                      title={t('dashboard.list.simulationTitle')}
                                    >
                                      💧 {t('dashboard.list.simulation')}
                                    </button>
                                  )}
                                </div>
                              )}
                              {/* LAS 포인트 클라우드는 투명도를 지원하지 않음(항상 불투명) — 메시 등 그 외 타일셋만 노출 */}
                              {isVisible && !isLasSource(item.originalFileName) && (
                                <div className="flex items-center gap-1.5 mt-1.5" onClick={e => e.stopPropagation()}>
                                  <span className="text-[9px] text-gray-400 shrink-0">{t('dashboard.list.opacity')}</span>
                                  <input
                                    type="range" min={0} max={100} step={1}
                                    value={getOpacity(item.id)}
                                    onChange={e => handleOpacityChange(item, Number(e.target.value))}
                                    className="flex-1 h-1 accent-blue-600 cursor-pointer"
                                  />
                                  <span className="text-[9px] text-gray-400 w-7 text-right shrink-0">{getOpacity(item.id)}%</span>
                                </div>
                              )}
                              {isVisible && isLasSource(item.originalFileName) && (
                                <div className="flex flex-col gap-1 mt-1.5 p-1.5 rounded bg-gray-50 border border-gray-100" onClick={e => e.stopPropagation()}>
                                  <select
                                    value={getPointStyle(item.id).mode}
                                    onChange={e => handlePointStyleChange(item, { mode: e.target.value as PointStyleState['mode'] })}
                                    className="text-[10px] border border-gray-200 rounded px-1 py-0.5 text-gray-600 bg-white"
                                  >
                                    {POINT_STYLE_MODES.map(m => <option key={m.val} value={m.val}>{t(m.labelKey)}</option>)}
                                  </select>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[9px] text-gray-400 shrink-0">{t('dashboard.list.pointSize')}</span>
                                    <input
                                      type="range" min={1} max={5} step={1}
                                      value={getPointStyle(item.id).pointSize}
                                      onChange={e => handlePointStyleChange(item, { pointSize: Number(e.target.value) })}
                                      className="flex-1 h-1 accent-blue-600 cursor-pointer"
                                    />
                                    <span className="text-[9px] text-gray-400 w-5 text-right shrink-0">{getPointStyle(item.id).pointSize}px</span>
                                  </div>
                                  <label className="flex items-center gap-1.5 text-[9px] text-gray-500 cursor-pointer select-none">
                                    <input
                                      type="checkbox"
                                      checked={getPointStyle(item.id).attenuation}
                                      onChange={e => handlePointStyleChange(item, { attenuation: e.target.checked })}
                                      className="accent-blue-600"
                                    />
                                    {t('dashboard.list.distanceAtten')}
                                  </label>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-center gap-1.5 shrink-0">
                              {isToggleable && (
                                <button
                                  onClick={e => { e.stopPropagation(); toggleTileset(item) }}
                                  title={isVisible ? t('dashboard.list.hideInViewer') : t('dashboard.list.showInViewer')}
                                  disabled={isLoadingThis || !!simulationTarget}
                                  className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${isLoadingThis || simulationTarget
                                    ? 'text-gray-300 cursor-wait'
                                    : isVisible
                                      ? 'text-blue-600 bg-blue-100 hover:bg-blue-200'
                                      : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'
                                    }`}
                                >
                                  {isLoadingThis ? (
                                    <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                    </svg>
                                  ) : isVisible ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                      <circle cx="12" cy="12" r="3" />
                                    </svg>
                                  ) : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                                      <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                  )}
                                </button>
                              )}
                              {!(item as MeshData & { sample?: boolean }).sample && (
                                <button
                                  onClick={e => { e.stopPropagation(); setDeleteModal({ id: item.id, name: item.name, hasOriginalFile: item.hasOriginalFile }) }}
                                  className="text-gray-300 hover:text-red-500 transition-colors"
                                  title={t('common.delete')}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
              </>
              )}

              {/* 사이드바 하단 — 너비 표시 */}
              <div className="px-4 py-2 border-t border-gray-100 shrink-0 flex items-center justify-between">
                <p className="text-[10px] text-gray-400">
                  © {new Date().getFullYear()} Gaia3D, Inc.
                </p>
                <p className="text-[10px] text-gray-300">{sidebarWidth}px</p>
              </div>
            </div>
          </>
        )}
      </div>

      {deleteModal && (
        <DeleteConfirmModal
          target={deleteModal}
          onDeleteOriginal={handleDeleteOriginal}
          onDeleteAll={handleDeleteAll}
          onCancel={() => setDeleteModal(null)}
        />
      )}
      {heightOffsetModal && (
        <HeightOffsetModal
          target={heightOffsetModal}
          onSubmit={handleApplyHeightOffset}
          onCancel={() => setHeightOffsetModal(null)}
          submitting={applyingHeightOffset}
        />
      )}
      {showMergeModal && (
        <MergeTilesetsModal
          candidates={items.filter(i => i.status === 'SUCCESS' && !!i.tileUrl && !i.sample)}
          onSubmit={handleMergeTilesets}
          onCancel={() => setShowMergeModal(false)}
          submitting={merging}
        />
      )}
      {showMultiMergeModal && (
        <MultiSourceMergeModal
          onClose={() => setShowMultiMergeModal(false)}
          onSuccess={() => {
            setShowMultiMergeModal(false)
            showToast(t('dashboard.toast.conversionStarted'))
            fetchData()
          }}
        />
      )}
      <EmailPromptModal open={!user?.email} />
      <ModeSelectModal
        open={activeModal === 'select'}
        onClose={() => setActiveModal(null)}
        onSelectMode={m => {
          if (m === 'merge') { setActiveModal(null); setShowMergeModal(true) }
          else if (m === 'multiMerge') { setActiveModal(null); setShowMultiMergeModal(true) }
          else setActiveModal(m)
        }}
      />
      <SimpleRegisterModal
        open={activeModal === 'simple'}
        onClose={() => setActiveModal(null)}
        onSuccess={() => { setActiveModal(null); fetchData() }}
        onSwitchToAdvanced={() => setActiveModal('advanced')}
      />
      <AdvancedRegisterModal
        open={activeModal === 'advanced'}
        onClose={() => setActiveModal(null)}
        onSuccess={() => { setActiveModal(null); fetchData() }}
        onSwitchToSimple={() => setActiveModal('simple')}
      />
    </div>
  )
}
